/**
 * Super-admin Onboarding funnel page. Shows:
 *   - A 3-stage funnel chart (% of tenants that reached each stage)
 *   - Counts at each stage
 *   - Top 25 "stuck" tenants with hours-since-started so we know who to
 *     personally nudge.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { PageHeader } from "@/components/Ui";
import { fmtDateTime } from "@/lib/time";
import { useTableControls, SortableTh, FilterChip } from "@/components/SortableTable";
import { TrendingDown, Clock, RotateCw } from "lucide-react";

const STAGE_LABEL = {
    email_verification: "Email verification",
    tour:               "Guided tour",
    complete:           "Completed",
};

export default function AdminOnboarding() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try { const { data } = await api.get("/admin/onboarding/funnel"); setData(data); }
        catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
    }, []);

    useEffect(() => { load(); }, [load]);  // eslint-disable-line

    return (
        <>
            <PageHeader title="Onboarding funnel"
                subtitle="Where do new firms drop off during onboarding? Reach out to anyone stuck for over a few hours."
                action={
                    <button onClick={load} disabled={loading} data-testid="onboarding-refresh"
                        className="inline-flex items-center gap-1.5 border border-border text-muted-foreground hover:text-foreground px-3 py-1.5 text-[10px] uppercase tracking-[0.16em]">
                        <RotateCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} strokeWidth={1.5}/>Refresh
                    </button>
                }/>

            {!data && <div className="text-sm text-muted-foreground py-8">Loading…</div>}

            {data && (
                <div className="space-y-6">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="onboarding-funnel-summary">
                        <Stat label="Total tenants" value={data.total_tenants} testId="onboarding-stat-total"/>
                        <Stat label="In email verification" value={data.counts.email_verification} testId="onboarding-stat-email"/>
                        <Stat label="In tour"               value={data.counts.tour} testId="onboarding-stat-tour"/>
                        <Stat label="Completed"             value={data.counts.complete} testId="onboarding-stat-complete"/>
                    </div>

                    <div className="border border-border bg-card p-5" data-testid="onboarding-funnel-chart">
                        <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground mb-3 inline-flex items-center gap-2">
                            <TrendingDown className="w-3.5 h-3.5" strokeWidth={1.5}/>Conversion funnel
                        </div>
                        <div className="space-y-2">
                            {data.funnel.map(f => (
                                <div key={f.stage} className="flex items-center gap-3" data-testid={`onboarding-funnel-bar-${f.stage}`}>
                                    <div className="text-xs text-foreground w-44 shrink-0">{STAGE_LABEL[f.stage] || f.stage}</div>
                                    <div className="flex-1 h-6 bg-background border border-border relative overflow-hidden">
                                        <div className="absolute inset-y-0 left-0 bg-primary/70" style={{ width: `${f.reached_pct}%` }}/>
                                        <span className="absolute inset-0 flex items-center justify-end px-2 text-[10px] text-foreground">
                                            {f.reached_pct}% · {f.reached} reached
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="border border-border bg-card" data-testid="onboarding-stuck-list">
                        <div className="px-5 py-3 border-b border-border inline-flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-muted-foreground">
                            <Clock className="w-3.5 h-3.5" strokeWidth={1.5}/>Currently stuck ({data.stuck.length})
                        </div>
                        {data.stuck.length === 0 ? (
                            <div className="px-5 py-8 text-center text-xs text-muted-foreground">Everyone&apos;s onboarded.</div>
                        ) : (
                            <StuckTable rows={data.stuck}/>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}

function Stat({ label, value, testId }) {
    return (
        <div className="border border-border bg-card p-3" data-testid={testId}>
            <div className="text-2xl font-semibold text-primary">{value}</div>
            <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mt-1">{label}</div>
        </div>
    );
}

function StuckTable({ rows }) {
    const ctrl = useTableControls(rows, {
        accessors: {
            firm: (r) => `${r.name} ${r.slug}`,
            stage: (r) => STAGE_LABEL[r.stage] || r.stage,
        },
    });
    return (
        <>
        <FilterChip ctrl={ctrl} className="m-2"/>
        <table className="w-full text-xs">
            <thead className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                <tr className="border-b border-border">
                    <SortableTh field="firm" ctrl={ctrl} className="text-left px-5 py-2">Firm</SortableTh>
                    <SortableTh field="stage" ctrl={ctrl} className="text-left px-5 py-2">Stage</SortableTh>
                    <SortableTh field="stuck_hours" ctrl={ctrl} className="text-right px-5 py-2" align="right">Stuck for</SortableTh>
                    <SortableTh field="email_verified_at" ctrl={ctrl} className="text-left px-5 py-2">Email verified</SortableTh>
                </tr>
            </thead>
            <tbody>
                {ctrl.rows.map(r => (
                    <tr key={r.tenant_id} className="border-b border-card last:border-0">
                        <td className="px-5 py-2.5">
                            <div className="text-foreground">{r.name}</div>
                            <div className="text-[10px] text-muted-foreground">{r.slug} · {r.status}</div>
                        </td>
                        <td className="px-5 py-2.5 text-primary">{STAGE_LABEL[r.stage] || r.stage}</td>
                        <td className="px-5 py-2.5 text-right text-muted-foreground">{r.stuck_hours == null ? "—" : `${r.stuck_hours} h`}</td>
                        <td className="px-5 py-2.5 text-muted-foreground">{r.email_verified_at ? fmtDateTime(r.email_verified_at) : <span className="text-muted-foreground">—</span>}</td>
                    </tr>
                ))}
            </tbody>
        </table>
        </>
    );
}
